import { useMemo } from "react"
import type { ColumnDef } from "@tanstack/react-table"
import type { CustomField } from "@/lib/types"
import { formatDate, formatCurrency } from "@/lib/utils"

/**
 * Generate TanStack Table column definitions from custom fields.
 * Expects entity rows to have a `custom_field_values` JSONB object
 * keyed by the custom field `key`.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useCustomFieldColumns<TData>(
  customFields: CustomField[] | undefined
): ColumnDef<TData, unknown>[] {
  return useMemo(() => {
    if (!customFields?.length) return []

    return customFields.map((field): ColumnDef<TData, unknown> => {
      const fieldLabel = field.label?.pl || field.label?.en || field.key

      return {
        id: `cf_${field.key}`,
        accessorFn: (row) => {
          const cfv = (row as Record<string, unknown>).custom_field_values as Record<string, unknown> | undefined
          return cfv?.[field.key] ?? null
        },
        header: fieldLabel,
        enableSorting: false,
        cell: ({ getValue }) => {
          const value = getValue()
          if (value == null || value === "") {
            return <span className="text-muted-foreground">—</span>
          }
          return (
            <span className="text-sm text-muted-foreground">
              {formatCustomFieldValue(value, field.field_type)}
            </span>
          )
        },
      }
    })
  }, [customFields])
}

function formatCustomFieldValue(value: unknown, fieldType: string): string {
  switch (fieldType) {
    case "date":
      return typeof value === "string" ? formatDate(value) : String(value)
    case "datetime":
      return typeof value === "string" ? formatDate(value) : String(value)
    case "number":
      return typeof value === "number" ? value.toLocaleString("pl-PL") : String(value)
    case "currency":
      return typeof value === "number" ? formatCurrency(value) : String(value)
    case "percentage":
      return typeof value === "number" ? `${value}%` : String(value)
    case "checkbox":
      return value ? "Tak" : "Nie"
    case "multi_select":
      return Array.isArray(value) ? value.join(", ") : String(value)
    case "rating":
      return typeof value === "number" ? "★".repeat(value) : String(value)
    default:
      return String(value)
  }
}
